import React, { useEffect, useState, useRef } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";

export default function CheckoutSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const purchaseType = params.get("type"); // "credits" or null (subscription)
  const isCredits = purchaseType === "credits";
  const [state, setState] = useState({ status: "polling", payment_status: null, package_id: null });
  const { refresh } = useAuth();
  const nav = useNavigate();
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setState({ status: "error", payment_status: "missing_session" });
      return;
    }
    let cancelled = false;

    const poll = async () => {
      if (cancelled) return;
      attempts.current += 1;
      try {
        const r = await api.get(`/checkout/status/${sessionId}`);
        const s = r.data;
        if (s.payment_status === "paid") {
          setState({ status: "paid", ...s });
          await refresh();
          // Auto-redirect for subscriptions — credits stay on the success page so
          // members see exactly how many credits landed.
          if (!isCredits) {
            setTimeout(() => {
              if (!cancelled) nav("/dashboard", { replace: true });
            }, 1800);
          }
          return;
        }
        if (s.status === "expired") {
          setState({ status: "expired", ...s });
          return;
        }
        if (attempts.current >= 30) {
          // 30 × 2s = 60s. Live Stripe webhooks can take 30-45s to fire — give them air.
          setState({ status: "timeout", ...s });
          return;
        }
        setTimeout(poll, 2000);
      } catch (e) {
        setState({ status: "error", payment_status: "error" });
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId, refresh, isCredits, nav]);

  return (
    <div className="min-h-screen bg-[#F9F6F0]">
      <Header />
      <section className="px-6 sm:px-12 lg:px-20 py-32 max-w-2xl mx-auto text-center">
        {state.status === "polling" && (
          <div data-testid="checkout-polling">
            <Loader2 className="w-12 h-12 text-[#C05C42] mx-auto mb-6 animate-spin" />
            <h1 className="font-serif text-3xl text-[#2D322B]">Confirming your offering…</h1>
            <p className="mt-4 text-[#5C6359]">A few breaths while the keepers sign the ledger.</p>
          </div>
        )}
        {state.status === "paid" && (
          <div data-testid="checkout-success">
            <CheckCircle2 className="w-14 h-14 text-[#2A3B2C] mx-auto mb-6" />
            {isCredits ? (
              <>
                <p className="label-eyebrow mb-3">Credits added</p>
                <h1 className="font-serif text-4xl text-[#2D322B]">{state.credits_granted || 25} credits added to your account.</h1>
                <p className="mt-4 text-[#5C6359]">Stack on top of your monthly allowance. Never expire.</p>
                <div className="mt-10 flex flex-wrap gap-4 justify-center">
                  <Link to="/biomarkers" className="btn-primary" data-testid="go-biomarkers-btn">Back to biomarkers</Link>
                  <Link to="/dashboard" className="btn-secondary" data-testid="go-dashboard-btn">Dashboard</Link>
                </div>
              </>
            ) : (
              <>
                <p className="label-eyebrow mb-3">Membership active</p>
                <h1 className="font-serif text-4xl text-[#2D322B]">You are welcomed. Badi opens.</h1>
                <p className="mt-4 text-[#5C6359]">Your <span className="capitalize">{state.package_id}</span> path begins now. Taking you to the dashboard…</p>
                <div className="mt-10 flex flex-wrap gap-4 justify-center">
                  <Link to="/dashboard" className="btn-primary" data-testid="go-dashboard-btn">Open dashboard now</Link>
                </div>
              </>
            )}
          </div>
        )}
        {(state.status === "expired" || state.status === "error") && (
          <div data-testid="checkout-failed">
            <XCircle className="w-12 h-12 text-[#8F3A3A] mx-auto mb-6" />
            <h1 className="font-serif text-3xl text-[#2D322B]">The session closed.</h1>
            <p className="mt-4 text-[#5C6359]">You can try again whenever the path feels right.</p>
            <div className="mt-10">
              <Link to="/pricing" className="btn-primary">Return to membership</Link>
            </div>
          </div>
        )}
        {state.status === "timeout" && (
          <div data-testid="checkout-timeout">
            <Loader2 className="w-12 h-12 text-[#D49A44] mx-auto mb-6" />
            <h1 className="font-serif text-3xl text-[#2D322B]">Your payment is being confirmed.</h1>
            <p className="mt-4 text-[#5C6359]">Stripe sometimes takes a minute to settle. Your subscription is almost certainly active — open the dashboard and pull-to-refresh, or check your email for the receipt.</p>
            <div className="mt-10 flex flex-wrap gap-4 justify-center">
              <Link to="/dashboard" className="btn-primary" data-testid="go-dashboard-btn">Open dashboard</Link>
              <Link to="/pricing" className="btn-secondary">Back to pricing</Link>
            </div>
          </div>
        )}
      </section>
      <Footer />
    </div>
  );
}
